import React, { useMemo, useState } from "react";

import {
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { useAllOrganizationUsers } from "src/api/taktile/queries";
import { Card } from "src/base-components/Card";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import {
  OrganizationUser,
  SimpleRoleType,
  WorkspaceRole,
} from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { User } from "src/flow/User";
import { DashboardPageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { CreateInviteModal } from "src/userManagement/CreateInviteModal/CreateInviteModal";
import { Status } from "src/userManagement/Status";
import { CellRendererProps, UserRow } from "src/userManagement/types";
import { UserActions } from "src/userManagement/UserActions";
import { WorkspaceRolesRenderer } from "src/userManagement/WorkspaceRolesRenderer";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const roleLabels: Record<SimpleRoleType, string> = {
  ADMIN: "Admin",
  MEMBER: "Team Member",
};

const USER_NAME_IS_NOT_DEFINED = "No username";

const cellRenderer = (
  info: CellRendererProps<string | undefined>,
  dataLoc?: string,
) => {
  return (
    <Cell dataLoc={dataLoc} info={info}>
      <span className="block max-w-90 truncate">{info.getValue()}</span>
    </Cell>
  );
};

const UserRenderer = (info: CellRendererProps<string>) => {
  const userId = useCurrentUserId();
  const value = info.getValue() || USER_NAME_IS_NOT_DEFINED;
  const user = info.row.original;
  return (
    <Cell dataLoc="user-name" info={info}>
      <span className="block max-w-90 truncate">
        <User
          avatar={user.avatar_url}
          name={userId === user.id ? `${value} (You)` : value}
        />
      </span>
    </Cell>
  );
};

const PermissionsRenderer = (info: CellRendererProps<SimpleRoleType>) => {
  const user = info.row.original;

  if (user.status === "deactivated") {
    return <Cell info={info} />;
  }

  return (
    <Cell info={info}>
      <Pill dataLoc={`org-role-${user.username}`} size="sm" variant="gray">
        <Pill.Text>{roleLabels[info.getValue()]}</Pill.Text>
      </Pill>
    </Cell>
  );
};

const columnHelper = createColumnHelper<UserRow>();

const Columns = [
  columnHelper.accessor("id", {
    header: (info) => (
      <Header info={info}>
        <span className="pl-1">ID</span>
        <Tooltip placement="top" title="User ID">
          <div className="ml-1">
            <Icon
              color="text-gray-500"
              cursorType="pointer"
              icon={faCircleInfo}
              size="2xs"
            />
          </div>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CopyTextIcon
          dataLoc="user-id"
          feedback="inline"
          tooltip="Copy user ID"
          value={info.getValue()}
        />
      </Cell>
    ),
    minSize: 55,
    maxSize: 55,
    meta: {
      sticky: "left",
    },
  }),
  columnHelper.accessor((row) => row.full_name || row.username, {
    id: "full_name_or_username",
    header: (info) => <Header info={info}>User</Header>,
    cell: UserRenderer,
    minSize: 200,
    maxSize: 200,
    filterFn: "includesString",
  }),
  columnHelper.accessor("email", {
    header: (info) => <Header info={info}>Email</Header>,
    cell: (info) => cellRenderer(info),
    maxSize: 200,
    minSize: 200,
    filterFn: "includesString",
  }),
  columnHelper.accessor("status", {
    header: (info) => <Header info={info}>Status</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Status
          dataLoc={`org-user-status-${info.row.original.username}`}
          status={info.row.original.status}
        />
      </Cell>
    ),
    minSize: 100,
    maxSize: 100,
  }),
  columnHelper.accessor("role", {
    header: (info) => <Header info={info}>Organization role</Header>,
    cell: (info) => (
      <PermissionsRenderer {...(info as CellRendererProps<SimpleRoleType>)} />
    ),
    minSize: 160,
    maxSize: 160,
  }),
  columnHelper.accessor("workspaces_roles", {
    header: (info) => <Header info={info}>Workspace permissions</Header>,
    cell: (info) => (
      <Cell info={info}>
        <WorkspaceRolesRenderer
          {...(info as unknown as CellRendererProps<WorkspaceRole[]>)}
        />
      </Cell>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: (info) => <Header info={info} />,
    cell: (info) => (
      <Cell dataLoc="user-actions" info={info}>
        {["invited", "active", "deactivated"].includes(
          info.row.original.status,
        ) && <UserActions user={info.row.original} />}
      </Cell>
    ),
    minSize: 60,
    maxSize: 60,
    meta: {
      sticky: "right",
    },
  }),
];

const UsersTable: React.FC<{
  users: OrganizationUser[];
  isLoading: boolean;
  searchQuery?: string;
}> = ({ users, isLoading, searchQuery }) => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const userId = useCurrentUserId();
  const [editUser, setEditUser] = useState<Nullable<OrganizationUser>>(null);

  const rows = useMemo(() => {
    const inviteRows = users
      .filter((u) => !u.authenticated)
      .map(
        (invite) =>
          ({
            ...invite,
            status: "invited",
            onEdit: () => setEditUser(invite),
          }) as UserRow,
      );

    const activeRows = users
      .filter((u) => u.is_active && u.authenticated)
      .map(
        (user) =>
          ({
            ...user,
            status: "active",
            onEdit: () => setEditUser(user),
          }) as UserRow,
      );

    const inactiveRows = users
      .filter((u) => !u.is_active)
      .map(
        (user) =>
          ({
            ...user,
            status: "deactivated",
          }) as UserRow,
      );

    return [...activeRows, ...inviteRows, ...inactiveRows];
  }, [users]);

  return (
    <>
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          <Table
            columns={Columns}
            data={rows}
            getRowId={(row) => row.id}
            globalFilter={searchQuery}
            isLoading={isLoading}
            clientSideFiltering
          />
        </Card.Content>
      </Card>
      <CreateInviteModal
        editableOrgRole={editUser?.id !== userId}
        open={Boolean(editUser)}
        organizationId={orgId}
        user={editUser}
        onClose={() => setEditUser(null)}
      />
    </>
  );
};

export const UserList: React.FC<{ searchQuery?: string }> = ({
  searchQuery,
}) => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const {
    isLoading,
    data: usersResult,
    isError,
    error,
  } = useAllOrganizationUsers(orgId, true);

  const users = usersResult?.pages.flat() ?? [];

  if (isError) {
    return (
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="min-h-0 flex-1">
          <EmptyState
            dataLoc="users-error-state"
            description={errorMessage(error)}
            headline="Error loading users"
            icon={faTriangleExclamation}
            variant="error"
          />
        </Card.Content>
      </Card>
    );
  }

  return (
    <UsersTable isLoading={isLoading} searchQuery={searchQuery} users={users} />
  );
};
